import React, { useEffect, useRef } from 'react';
import katex from 'katex';

/**
 * LaTeXText - Component that renders text with LaTeX formulas
 * Automatically detects and renders $$...$$ patterns using KaTeX
 * Supports both plain text/Markdown and HTML content
 */
export function LaTeXText({ children, style = {}, className = '' }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !children) return;

    let html = String(children);
    
    // First, set the HTML content to preserve existing HTML structure
    containerRef.current.innerHTML = html;
    
    // Then, walk through all text nodes and replace $$...$$ patterns
    const replaceLatexInNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        if (text && /\$\$.*?\$\$/.test(text)) {
          // Create a temporary div to parse the replaced HTML
          const tempDiv = document.createElement('div');
          const rendered = text.replace(/\$\$(.*?)\$\$/g, (match, latex) => {
            try {
              return katex.renderToString(latex, {
                displayMode: false,
                throwOnError: false,
                output: 'html',
              });
            } catch (e) {
              console.error('KaTeX rendering error:', e);
              return `<span style="color: red; font-family: monospace;">${latex}</span>`;
            }
          });
          tempDiv.innerHTML = rendered;
          
          // Replace the text node with the rendered content
          const fragment = document.createDocumentFragment();
          while (tempDiv.firstChild) {
            fragment.appendChild(tempDiv.firstChild);
          }
          node.parentNode.replaceChild(fragment, node);
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Recursively process child nodes
        Array.from(node.childNodes).forEach(replaceLatexInNode);
      }
    };
    
    replaceLatexInNode(containerRef.current);
  }, [children]);

  return <div ref={containerRef} style={style} className={className} />;
}

export default LaTeXText;
